import {
  DOOR_AREA_SIZE,
  WINDOW_AREA_SIZE,
} from '@molecules/OpeningsSelector/OpeningsSelector';

export interface Wall {
  length: string;
  height: string;
  width?: string;
}

const METER_TO_FEET = 3.28084;
const M2_TO_FT2 = METER_TO_FEET * METER_TO_FEET;

export interface PaintResult {
  displayArea: number; // in current unit
  litres: number;
}

/**
 * Calculates net area (subtracting doors/windows) and paint needed.
 *
 * @param walls           array of { length, height } strings
 * @param doorsCount      number of doors
 * @param windowsCount    number of windows
 * @param unit            'm' | 'ft'
 * @param coverage        m² of coverage per litre
 */
export function calculatePaint(
  walls: Wall[],
  doorsCount: number,
  windowsCount: number,
  unit: 'm' | 'ft',
  coverage: number,
  coats: number,
): PaintResult {
  let rawArea = walls.reduce((sum, { length, height, width }) => {
    const L = parseFloat(length.replace(',', '.'));
    const H = parseFloat(height.replace(',', '.'));
    if (width !== undefined) {
      const W = parseFloat(width.replace(',', '.'));
      if (isNaN(L) || isNaN(H) || isNaN(W)) return sum;
      const roomWallArea = L * H * 2 + W * H * 2;
      return sum + roomWallArea;
    }
    if (isNaN(L) || isNaN(H)) return sum;
    return sum + L * H;
  }, 0);

  const openingsM2 =
    doorsCount * DOOR_AREA_SIZE + windowsCount * WINDOW_AREA_SIZE;

  if (unit === 'ft') {
    rawArea = rawArea / M2_TO_FT2;
  }

  const netM2 = Math.max(0, rawArea - openingsM2);

  const litres =
    coverage > 0 ? parseFloat(((netM2 / coverage) * coats).toFixed(2)) : 0;

  const displayArea =
    unit === 'm' ? netM2 : parseFloat((netM2 * M2_TO_FT2).toFixed(2));

  return {
    displayArea: parseFloat(displayArea.toFixed(2)),
    litres,
  };
}
